/**
 * PlsCompetitionFixturePicker — single-source (PLS only) Competition /
 * Season / Date / Fixture picker.
 *
 * Structurally modeled on MergedFixturePicker.jsx's Competition/Season/Date/
 * Fixture UI, but without any FIFA branch, "All competitions" pseudo-option,
 * or competitionKey prefix scheme — this picker only ever talks to one
 * IFixtureCatalogue (DdsPlsCatalogue), so onSelect reports just (id, label),
 * no `source` tag.
 *
 * Single Responsibility: render the cascade; report selection + browse
 * changes via callback props. Knows nothing about DDS/broker/session
 * concerns — the parent owns building/rebuilding the catalogue.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, TextField, MenuItem, CircularProgress, Popover } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FixtureCalendar from "./FixtureCalendar";

const FIELD_SX = {
  "& .MuiInputBase-input": { color: "var(--text)" },
  "& .MuiSelect-icon": { color: "var(--text-muted)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-strong)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--green)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--green)" },
};
const MENU_PROPS = {
  MenuProps: {
    PaperProps: {
      sx: {
        backgroundColor: "var(--elevated)", color: "var(--text)",
        "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(69, 255, 167, 0.1)" },
        "& .Mui-selected": { backgroundColor: "rgba(69, 255, 167, 0.15) !important" },
      },
    },
  },
};

// Multi-token AND filter across label/home/away/tricodes/venue/id — ported
// from PlsFixturePicker.jsx's _matchesAllTokens (richer than a label-only
// match since there's no competitionLabel tagging concern here).
function matchesAllTokens(option, tokens) {
  if (tokens.length === 0) return true;
  const haystack = [
    option.label,
    option.home,
    option.away,
    option.homeTricode,
    option.awayTricode,
    option.raw?.venue?.[0]?.venue_name,
    option.id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return tokens.every((tok) => haystack.includes(tok));
}

// The LOCAL calendar day, as YYYY-MM-DD — the same day the Date field, the
// calendar and each fixture's label show. The UTC day put a late kick-off
// (e.g. 20:00 UTC = 01:30 IST next day) under a different date than its label.
const pad2 = (n) => String(n).padStart(2, "0");
const dayOf = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// Puts the already-selected {id, name} at the front of a list that hasn't
// loaded it yet, so its select shows the value instead of blank.
const withSelected = (list, id, name) =>
  (id && name && !list.some((item) => item.id === id) ? [{ id, name }, ...list] : list);

export default function PlsCompetitionFixturePicker({
  catalogue,
  competitions = [],
  competitionId,
  onCompetitionChange,
  seasons = [],
  seasonId,
  onSeasonChange,
  // Optional display names for the current competition/season (e.g. stored on
  // a saved session) so both selects render filled-in while their lists load.
  selectedCompetitionName = null,
  selectedSeasonName = null,
  selectedFixtureId,
  // Optional { id, label, startDateTime } describing the already-selected
  // fixture (e.g. from a saved session) so it renders immediately instead of
  // waiting for listFixtures() to resolve.
  selectedFixtureFallback = null,
  onSelect,
  size = "small",
  // Two-column layout for wide containers: Competition | Season, then a
  // narrow Date beside a wide Fixture. Stacks on small screens.
  wide = false,
}) {
  // A pre-selected fixture (selectedFixtureFallback) starts the Date filter on
  // its own day rather than "All dates".
  const fallbackDay = selectedFixtureFallback?.id && selectedFixtureFallback.id === selectedFixtureId
    ? dayOf(selectedFixtureFallback.startDateTime) : "";
  const [dateFilter, setDateFilter] = useState(fallbackDay);
  const [dateAnchor, setDateAnchor] = useState(null); // calendar popover anchor
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch fixtures whenever the (parent-rebuilt) catalogue changes.
  useEffect(() => {
    if (!catalogue) { setFixtures([]); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    catalogue.listFixtures()
      .then((rows) => { if (!cancelled) setFixtures(rows || []); })
      .catch((err) => { if (!cancelled) { setError(err?.message || String(err)); setFixtures([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [catalogue]);

  // The date filter is scoped to the current competition/season's fixtures —
  // drop it when either changes so a stale day can't blank the list. Parents
  // seed competition/season after mount, so fall back to the pre-selected
  // fixture's day while it's still the selection. fallbackDay is read, not a
  // dependency: picking another fixture must not reset the user's date.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDateFilter(fallbackDay); }, [competitionId, seasonId]);

  const competitionOptions = useMemo(
    () => withSelected(competitions, competitionId, selectedCompetitionName),
    [competitions, competitionId, selectedCompetitionName],
  );
  const seasonOptions = useMemo(
    () => withSelected(seasons, seasonId, selectedSeasonName),
    [seasons, seasonId, selectedSeasonName],
  );

  const dateOptions = useMemo(
    () => Array.from(new Set(fixtures.map((f) => dayOf(f.startDateTime)).filter(Boolean))).sort(),
    [fixtures],
  );
  const visibleFixtures = useMemo(
    () => (dateFilter ? fixtures.filter((f) => dayOf(f.startDateTime) === dateFilter) : fixtures),
    [fixtures, dateFilter],
  );
  const loadedSelected = visibleFixtures.find((f) => f.id === selectedFixtureId)
    || fixtures.find((f) => f.id === selectedFixtureId);
  const fallbackSelected = !loadedSelected && selectedFixtureId && selectedFixtureFallback?.id === selectedFixtureId
    ? selectedFixtureFallback : null;
  const selected = loadedSelected || fallbackSelected;
  // Keep the fallback in the option list so Autocomplete accepts it as a value.
  const options = fallbackSelected ? [fallbackSelected, ...visibleFixtures] : visibleFixtures;

  // Picking a day the selected fixture isn't on drops the selection, so the
  // form can't save a fixture whose date disagrees with the Date shown. "All
  // dates" keeps it — it doesn't contradict any fixture.
  const handleDateChange = (day) => {
    setDateFilter(day);
    if (day) setDateAnchor(null);
    if (day && selected && dayOf(selected.startDateTime) !== day) onSelect(null, null, null);
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: wide && seasonOptions.length > 0 ? { xs: "1fr", sm: "1fr 1fr" } : "1fr",
      }}>
        <TextField select label="Competition" size={size} fullWidth value={competitionId || ""}
          onChange={(e) => onCompetitionChange?.(e.target.value)}
          sx={FIELD_SX} SelectProps={MENU_PROPS}>
          {competitionOptions.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>

        {seasonOptions.length > 0 && (
          <TextField select label="Season" size={size} fullWidth value={seasonId || ""}
            onChange={(e) => onSeasonChange?.(e.target.value)}
            sx={FIELD_SX} SelectProps={MENU_PROPS}>
            {seasonOptions.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      <Box sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: wide ? { xs: "1fr", sm: "1fr 2fr" } : "1fr",
      }}>
        <TextField
          select={false}
          label="Date"
          size={size}
          fullWidth
          value={dateFilter ? new Date(`${dateFilter}T00:00:00`).toLocaleDateString() : "All dates"}
          onClick={(e) => setDateAnchor(e.currentTarget)}
          sx={{ ...FIELD_SX, "& .MuiInputBase-root": { cursor: "pointer" }, "& input": { cursor: "pointer" } }}
          InputProps={{
            readOnly: true,
            endAdornment: <CalendarMonthIcon sx={{ color: "var(--text-muted)", fontSize: 18 }} />,
          }}
        />
        <Popover
          open={Boolean(dateAnchor)}
          anchorEl={dateAnchor}
          onClose={() => setDateAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{ paper: { sx: { backgroundColor: "transparent", boxShadow: 6, mt: 0.5, overflow: "visible" } } }}
        >
          <Box sx={{ width: 280 }}>
            <FixtureCalendar
              markedDates={dateOptions}
              value={dateFilter}
              onChange={handleDateChange}
            />
          </Box>
        </Popover>

        <Autocomplete
          fullWidth
          size={size}
          options={options}
          loading={loading}
          value={selected}
          onChange={(_, next) => onSelect(next ? next.id : null, next ? next.label : null, next || null)}
          getOptionLabel={(opt) => {
            if (!opt) return "";
            const date = opt.startDateTime ? ` (${new Date(opt.startDateTime).toLocaleDateString()})` : "";
            return `${opt.label}${date}`;
          }}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(opts, state) => {
            const tokens = state.inputValue.toLowerCase().split(/\s+/).filter(Boolean);
            return opts.filter((opt) => matchesAllTokens(opt, tokens));
          }}
          renderInput={(params) => (
            <TextField {...params} label="Fixture" variant="outlined" sx={FIELD_SX}
              InputProps={{
                ...params.InputProps,
                // No spinner while a fallback is already shown — the field has its value.
                endAdornment: (<>{loading && !fallbackSelected ? <CircularProgress color="inherit" size={18} /> : null}{params.InputProps.endAdornment}</>),
              }} />
          )}
          renderOption={(props, option) => {
            const { key: _ignored, ...rest } = props;
            return (
              <li key={option.id} {...rest}>
                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</span>
                  {option.startDateTime && (
                    <span style={{ fontSize: "0.7rem", color: "var(--text-dim)" }}>
                      {new Date(option.startDateTime).toLocaleDateString()}
                    </span>
                  )}
                </Box>
              </li>
            );
          }}
          noOptionsText={error ? `Error: ${error}` : "No fixtures match"}
        />
      </Box>
    </Box>
  );
}
